/**
 * Realtime notifications hook with Supabase subscription
 */

import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../../../lib/supabase'
import { useAuth } from '../../../contexts/auth-context'
import { useNotifications, useMarkNotificationRead } from './use-dashboard'
import type { Notification } from '../types/dashboard.types'

export function useRealtimeNotifications() {
  const { user } = useAuth()
  const { notifications: initialNotifications, loading: initialLoading, error: initialError } = useNotifications()
  const { markAsRead: markAsReadMutation, loading: markLoading, error: markError } = useMarkNotificationRead()

  const [notifications, setNotifications] = useState<Notification[]>(initialNotifications)
  const [loading, setLoading] = useState(initialLoading)
  const [error, setError] = useState<Error | null>(initialError)

  // Sync with initial notifications from useNotifications
  useEffect(() => {
    setNotifications(initialNotifications)
    setLoading(initialLoading)
    setError(initialError)
  }, [initialNotifications, initialLoading, initialError])

  // Calculate unread count
  const unreadCount = notifications.filter(n => !n.is_read).length

  // Realtime subscription
  useEffect(() => {
    if (!user?.id) return

    const channel = supabase
      .channel('user-notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          const newNotification = payload.new as Notification
          // Prepend new notification to the list
          setNotifications(prev => [newNotification, ...prev])
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [user?.id])

  // Mark as read handler
  const markAsRead = useCallback(
    async (notificationId: string) => {
      try {
        await markAsReadMutation(notificationId)
        // Remove from local list immediately for optimistic update
        setNotifications(prev => prev.filter(n => n.id !== notificationId))
      } catch (err) {
        console.error('Failed to mark notification as read:', err)
        setError(err instanceof Error ? err : new Error('Failed to mark notification as read'))
      }
    },
    [markAsReadMutation]
  )

  return {
    notifications,
    unreadCount,
    markAsRead,
    loading: loading || markLoading,
    error: error || markError
  }
}
